import PauseCircleFilledOutlinedIcon from '@mui/icons-material/PauseCircleFilledOutlined';
import PlayCircleFilledRoundedIcon from '@mui/icons-material/PlayCircleFilledRounded';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import { Box, Grid, Typography } from '@mui/material';
import Axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import Webcam from 'react-webcam';
import CustomisedButton from '../../components/button/button';
import CircularProgressBar from '../../components/progressBar/CircularProgressBar';
import StopWatch from '../../components/stopWatch/stopWatch';
import { APIBASE_URL, KYC_URL, UUID } from '../../config/cacheKey';
import { ApiResultModel, IDocument } from '../../models/common.model';
import { IGetVideoRequest, ISaveVideoRequest } from '../../models/kyc.model';
import { changeRouteType, getNextInnerState, getpervInnerState } from '../../redux/handleBackBtn/handleBackBtnSlice';
import { setHeaderTitle } from '../../redux/headerTitle/headerTitleSlice';
import { RootState } from '../../redux/store';
import { GetVideo } from '../../services/kyc.service';
import './captureVideo.css';
import { useNavigate } from 'react-router-dom';
import { IVideoAuthorization } from './IVideoAuthorization';
import ConfirmDeleteModal from '../../components/modal/confirmDeleteModal';
import InfoModal from '../../components/modal/infoModal/infoModal';

const videoConstraints = {
    width: 320,
    height: 320,
    frameRate: 24,
    facingMode: 'user',
};

const CaptureVideo = ({ setStep, referenceKey }: IVideoAuthorization) => {
    const navigate = useNavigate();
    const [recordedVideo, setRecordedVideo] = useState<any>(false);
    const [recordedChunks, setRecordedChunks] = useState<any>([]);
    const [capturing, setCapturing] = useState(false);
    const [, setSendVideo] = useState('show');
    const [time, setTime] = useState(0);
    const mediaRecorderRef = useRef<any>(null);
    const webcamRef = useRef<any>(null);
    const [isRunning, setIsRunning] = useState(false);
    const [resetTime, setResetTime] = useState(false);
    const [sizeLimit, setSizeLimit] = useState(false);
    const [isPlaying, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [hasSeend, setSeend] = useState(false);
    const [videoPlaying, setVideoPlaying] = useState(false);
    const [openErrorModal, setOpenErrorModal] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isServiceLoaded, setIsServiceLoaded] = useState(false);


    const videoText = useSelector(
        (state: RootState) => state.videoText.textContent
    );
    const randomKeyForDispatch = Math.random();
    const videoRef = useRef<HTMLVideoElement>(null)
    const recordedChunksRef = useRef<any>([]);
    const videoUrlRef = useRef<any>(null);
    const videoSizeLimit = 5 * 1000 * 1000;

    const dispatch = useDispatch();
    const handleDataAvailable = useCallback(
        ({ data }: BlobEvent) => {
            if (data.size > 0) {
                recordedChunksRef.current.push(data);

                if (data.size > videoSizeLimit) {
                    setSizeLimit(true);
                }
            }
        },
        [videoSizeLimit]
    );


    function recordAgain() {
        setCapturing(false);
        recordedChunksRef.current = [];
        setRecordedVideo(false);
        setSendVideo('show');
        setResetTime(true);
        setSizeLimit(false);
        setSeend(false);
        setPlaying(false);
    }

    const handleStartCaptureClick = useCallback(() => {
        setCapturing(true);
        setIsRunning(true);
        setResetTime(false);
        recordedChunksRef.current = [];
        if (webcamRef.current && webcamRef.current.stream) {
            mediaRecorderRef.current = new MediaRecorder(
                webcamRef.current.stream,
                {
                    mimeType: MediaRecorder.isTypeSupported('video/webm; codecs=vp9')
                        ? 'video/webm; codecs=vp9'
                        : 'video/mp4',
                    videoBitsPerSecond: 1000000,
                    audioBitsPerSecond: 128000,
                }
            );

            mediaRecorderRef.current.addEventListener('dataavailable', handleDataAvailable);
            mediaRecorderRef.current.start();
        } else {
            console.error('Webcam stream is not available');
        }
    }, [handleDataAvailable]);


    const handleStopCaptureClick = useCallback(() => {
        setIsRunning(false);
        setCapturing(false)
        setPlaying(true);
        setSeend(true);
        if (mediaRecorderRef.current) {
            mediaRecorderRef.current.stop();
            mediaRecorderRef.current.onstop = () => {
                try {
                    if (recordedChunksRef.current.length > 0) {
                        const blob = new Blob(recordedChunksRef.current, { type: 'video/mp4' });
                        const url = URL.createObjectURL(blob);

                        if (videoUrlRef.current) {
                            URL.revokeObjectURL(videoUrlRef.current);
                        }

                        videoUrlRef.current = url;
                        setRecordedVideo(url);
                    } else {
                        console.error('No recorded chunks available.');
                    }
                } catch (error) {
                    console.error('Error creating video URL:', error);
                }

            };
        }
    }, []);

    const handleUpdload = useCallback(() => {
        if (recordedChunksRef.current.length > 0) {
            const blob = new Blob(recordedChunksRef.current, { type: 'video/mp4' });

            SaveVideoMutate({
                data: blob,
                referenceKey: referenceKey!,
            });
        }
    }, [recordedChunks]);
    const SaveVideo = async (
        data: ISaveVideoRequest
    ): Promise<ApiResultModel<IDocument>> => {
        const formData = new FormData();
        formData.append('Data', data.data!);
        formData.append('ReferenceKey', data.referenceKey!);

        return Axios.post<ApiResultModel<IDocument>>(
            APIBASE_URL + KYC_URL + '/SaveVideo',
            formData,
            { onUploadProgress }
        ).then((result) => {
            return result.data;
        });
    };

    const onUploadProgress = (event: any) => {
        const percentCompleted = Math.round((event.loaded * 100) / event.total);
        setProgress(percentCompleted);
    };

    const { mutate: SaveVideoMutate, isLoading } = useMutation(SaveVideo, {
        onSuccess: (data: ApiResultModel<IDocument>) => {
            if (data) {
                navigate('/loans/request');
            }
        },
        onError: () => { },
    });

    const { mutate: GetVideoMutate, isLoading: GetVideoLoading } = useMutation(GetVideo, {
        onSuccess: (data: IGetVideoRequest) => {
            if (data) {
                setRecordedVideo(data.fileUrl);
                setCapturing(false)
                recordedChunksRef.current.push(['test'])
            }
        },
        onError: () => { },
    });


    const togglePlayPause = () => {
        if (videoRef.current) {
            if (videoPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setVideoPlaying(!videoPlaying);
        }
    };

    const handleConfirmError = () => {
        navigate('/loans/request');

    }

    const handleMediaError = (error: DOMException) => {
        if (!recordedVideo && isServiceLoaded) {
            setOpenErrorModal(true);
            if (error.name === 'NotAllowedError') {
                setErrorMessage('لطفا اجازه دسترسی به دوربین را بدهید.');
            } else if (error.name === 'NotFoundError') {
                setErrorMessage('دوربین یافت نشد.');
            } else if (error.name === 'NotReadableError' || error.name === 'OverconstrainedError') {
                setErrorMessage('دوربین قابل دسترسی نیست.');
            } else {
                setErrorMessage('خطایی در دسترسی به دوربین رخ داده است.');
            }
        }
    }

    useEffect(() => {
        if (!GetVideoLoading) {
            setIsServiceLoaded(true);
        }
    }, [GetVideoLoading]);

    useEffect(() => {
        GetVideoMutate({ referenceKey: referenceKey })
        dispatch(setHeaderTitle({ header: 'احراز هویت ویدیوئی', color: 'var(--header-color)', parentRoute: 'kyc' }));
        dispatch(changeRouteType({ routeType: 'child' }));
        dispatch(getpervInnerState({ prevInnerState: `videoHint ${randomKeyForDispatch}` }));
    }, [])

    useEffect(() => {
        if ((time % 6000) / 100 === 20) {
            handleStopCaptureClick();
        }
    }, [time]);


    useEffect(() => {
        const videoElement = videoRef.current;
        if (videoElement) {
            const handleEnded = () => {
                setVideoPlaying(false);
            };
            videoElement.addEventListener('ended', handleEnded);
            return () => {
                videoElement.removeEventListener('ended', handleEnded);
            };
        }
    }, [videoRef.current]);


    return (
        <div className="relative h-full px-5">
            {
                <Grid
                    container
                    flexDirection={'column'}
                    justifyContent={'space-between'}
                    alignItems={'center'}
                >
                    <Grid
                        item
                    >
                        <Grid item>

                            {!hasSeend ? (
                                <Typography
                                    color={'var(--primary-text-color)'}
                                    fontSize={'.9rem'}
                                    fontWeight={'bold'}
                                    textAlign={'center'}
                                    sx={{ marginTop: "10px" }}
                                >
                                    پس از فشردن دکمه "شروع" متن زیر را با صدای رسا بخوانید.
                                </Typography>
                            ) : (
                                !isLoading && !sizeLimit && (
                                    <Typography
                                        marginY={"20px"}
                                        color={'var(--primary-text-color)'}
                                        fontSize={".9rem"}
                                        fontWeight={'bold'}
                                        textAlign={'center'}
                                        sx={{ marginTop: "10px" }}
                                    >
                                        در صورت تایید ویدئو دکمه "بارگذاری و ارسال" را بزنید.
                                    </Typography>
                                )
                            )}
                            {!hasSeend && (
                                <Typography
                                    textAlign={'center'}
                                    color={'var(--primary-text-color)'}
                                    // fontWeight={"600"}
                                    fontSize={'.9rem'}
                                    marginY={'20px'}
                                >
                                    {videoText}
                                </Typography>
                            )}
                            {sizeLimit && (
                                <><Typography
                                    color={'var(--primary-text-color)'}
                                    fontSize={'.9rem'}
                                    fontWeight={'bold'}
                                    textAlign={'center'}
                                    marginBottom={'20px'}
                                >
                                    لطفا دکمه "ضبط مجدد" را فشرده و دوباره امتحان کنید.
                                </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: '.9rem',
                                            color: '#FF617C',
                                            textAlign: 'center',
                                            marginBottom: "10px"
                                        }}
                                    >


                                        حجم ویدئو شما از 5 مگابایت رد شده است.
                                        <br />
                                        لطفا متن را با سرعت بیشتری بخوانید
                                        .                                </Typography></>
                            )}

                            <div>
                                <div className="flex h-full flex-col justify-center gap-10">
                                    <div className="flex flex-col justify-between gap-2">
                                        <div>
                                            <>
                                                <div className="flex justify-center">
                                                    {/* webcam video */}
                                                    <div
                                                        className=
                                                        {!isLoading ? 'upload-identication-image-container-border-outlined' : ""}
                                                    >
                                                        {recordedVideo ? (
                                                            !isLoading && (
                                                                <video
                                                                    ref={videoRef}
                                                                    style={{
                                                                        borderRadius:
                                                                            '160px',
                                                                    }}
                                                                    // width={1280}
                                                                    // height={720}
                                                                    src={
                                                                        recordedVideo
                                                                    }
                                                                    controlsList="nodownload"
                                                                    playsInline
                                                                    // controls
                                                                    muted={
                                                                        false
                                                                    }
                                                                />
                                                            )
                                                        ) : (
                                                            <div>
                                                                <Webcam
                                                                    // height={320}
                                                                    // width={320}
                                                                    mirrored={
                                                                        true
                                                                    }
                                                                    audio={true}
                                                                    ref={
                                                                        webcamRef
                                                                    }
                                                                    videoConstraints={
                                                                        videoConstraints
                                                                    }
                                                                    style={{
                                                                        borderRadius:
                                                                            '160px',
                                                                    }}
                                                                    muted={true}
                                                                    onUserMediaError={(err) => { if (isServiceLoaded) handleMediaError(err as DOMException) }}

                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {!isPlaying && (
                                                    <span className="flex justify-center mt-3">
                                                        <StopWatch
                                                            isRunning={
                                                                isRunning
                                                            }
                                                            setIsRunning={
                                                                setIsRunning
                                                            }
                                                            stopTime={20}
                                                            reset={resetTime}
                                                            time={time}
                                                            setTime={setTime}
                                                        />
                                                    </span>
                                                )}
                                            </>
                                        </div>
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'center',
                                                // marginTop: "50px",

                                                height: '100%',
                                            }}
                                        >
                                            {recordedChunksRef.current.length > 0 ? (
                                                <div className="flex flex-col gap-3 mt-4">
                                                    <Grid
                                                        container
                                                        flexDirection={'column'}
                                                        gap={'10px'}
                                                    >
                                                        {/* delete & play */}

                                                        {!isLoading && (
                                                            <Box
                                                                sx={{
                                                                    display:
                                                                        'flex',
                                                                    gap: '20px',
                                                                }}
                                                            >
                                                                <Grid
                                                                    container
                                                                    flexDirection={
                                                                        'column'
                                                                    }
                                                                    justifyContent={
                                                                        'center'
                                                                    }
                                                                    alignItems={
                                                                        'center'
                                                                    }

                                                                >
                                                                    {!videoPlaying ? (<><Grid item>
                                                                        <PlayCircleFilledRoundedIcon
                                                                            sx={{
                                                                                fontSize: '50px',
                                                                                color: '#BBA57A',
                                                                            }}
                                                                            onClick={
                                                                                togglePlayPause} />
                                                                    </Grid><Grid item marginTop={"3px"}>
                                                                            <Typography
                                                                                sx={{
                                                                                    color: 'var(--primary-text-color)',
                                                                                    fontSize: '.8rem',
                                                                                }}
                                                                            >
                                                                                پخش
                                                                            </Typography>

                                                                        </Grid></>) :
                                                                        (<><Grid item>
                                                                            <PauseCircleFilledOutlinedIcon
                                                                                sx={{
                                                                                    fontSize: '50px',
                                                                                    color: '#BBA57A',
                                                                                }}
                                                                                onClick={
                                                                                    togglePlayPause} />
                                                                        </Grid><Grid item marginTop={"3px"}>
                                                                                <Typography
                                                                                    sx={{
                                                                                        color: 'var(--primary-text-color)',
                                                                                        fontSize: '.8rem',
                                                                                    }}
                                                                                >
                                                                                    وقفه
                                                                                </Typography>

                                                                            </Grid></>)
                                                                    }
                                                                </Grid>
                                                                <Grid
                                                                    container
                                                                    flexDirection={
                                                                        'column'
                                                                    }
                                                                    justifyContent={
                                                                        'center'
                                                                    }
                                                                    alignItems={
                                                                        'center'
                                                                    }
                                                                >
                                                                    <Grid item>
                                                                        <ReplayRoundedIcon
                                                                            sx={{
                                                                                fontSize:
                                                                                    '50px',
                                                                                color: 'var(--primary-text-color)',
                                                                            }}
                                                                            onClick={
                                                                                recordAgain
                                                                            }
                                                                        />
                                                                    </Grid>
                                                                    <Grid item marginTop={"3px"}>
                                                                        <Typography
                                                                            sx={{
                                                                                color: 'var(--primary-text-color)',
                                                                                fontSize:
                                                                                    '.8rem',
                                                                                textWrap:
                                                                                    'nowrap',
                                                                            }}
                                                                        >
                                                                            ضبط
                                                                            مجدد
                                                                        </Typography>
                                                                    </Grid>
                                                                </Grid>
                                                            </Box>
                                                        )}

                                                        <Grid item>
                                                            <>
                                                                {/* send */}
                                                                {isLoading ? (
                                                                    <div className="absolute right-1/2 top-1/4 translate-x-1/2 translate-y-1/2">
                                                                        <CircularProgressBar
                                                                            progress={
                                                                                progress
                                                                            }
                                                                        />
                                                                        <Typography
                                                                            sx={{
                                                                                color: 'var(--primary-text-color)',
                                                                                fontWeight:
                                                                                    'bold',
                                                                                marginTop:
                                                                                    '1rem',
                                                                                whiteSpace: "nowrap"
                                                                            }}
                                                                        >
                                                                            لطفا
                                                                            تا
                                                                            ارسال
                                                                            کامل
                                                                            ویدئو
                                                                            شکیبا
                                                                            باشید
                                                                        </Typography>
                                                                    </div>
                                                                ) : (
                                                                    !sizeLimit &&
                                                                    hasSeend && (
                                                                        <div className="absolute bottom-[10px] !m-[auto] left-0 right-0 w-full px-[20px] mt-8">
                                                                            <CustomisedButton
                                                                                disable={
                                                                                    recordedChunksRef.current.length <=
                                                                                    0 ||
                                                                                    isLoading
                                                                                }
                                                                                onClick={
                                                                                    handleUpdload
                                                                                }
                                                                                title="بارگذاری و ارسال"
                                                                                type="button"
                                                                            />
                                                                        </div>
                                                                    )
                                                                )}
                                                            </>
                                                        </Grid>
                                                    </Grid>
                                                </div>
                                            ) : capturing ? (
                                                <>
                                                    <Grid
                                                        container
                                                        justifyContent={
                                                            'center'
                                                        }
                                                        alignItems={'center'}
                                                        flexDirection={'column'}
                                                        gap={"3px"}
                                                    >
                                                        <Grid item>
                                                            <StopCircleOutlinedIcon
                                                                sx={{
                                                                    fontSize:
                                                                        '50px',
                                                                    color: '#BBA57A',
                                                                }}
                                                                onClick={


                                                                    handleStopCaptureClick


                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid item>
                                                            <Typography
                                                                sx={{
                                                                    color: 'var(--primary-text-color)',
                                                                    fontSize:
                                                                        '.8rem',
                                                                }}
                                                            >
                                                                توقف
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </>
                                            ) : (
                                                <>
                                                    <Grid
                                                        container
                                                        flexDirection={'column'}
                                                        justifyContent={
                                                            'center'
                                                        }
                                                        alignItems={'center'}
                                                        gap={"3px"}
                                                    >
                                                        <Grid item>
                                                            <RadioButtonCheckedIcon
                                                                sx={{
                                                                    fontSize:
                                                                        '50px',
                                                                    color: '#FF617C',
                                                                }}
                                                                onClick={
                                                                    handleStartCaptureClick
                                                                }
                                                            />
                                                        </Grid>
                                                        <Grid item>
                                                            <Typography
                                                                sx={{
                                                                    color: 'var(--primary-text-color)',
                                                                    fontSize:
                                                                        '.8rem',
                                                                }}
                                                            >
                                                                شروع
                                                            </Typography>
                                                        </Grid>
                                                    </Grid>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </Grid>
            }
            <InfoModal content={errorMessage} onConfirm={handleConfirmError} openModal={openErrorModal} />
        </div>
    );
};

export default CaptureVideo;
